import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import { useModal } from '../context/ModalContext';
import { getUserRole } from '../utils/roleHierarchy';
import '../styles/DatabaseBackups.css';
import CustomSelect from './CustomSelect';

const DatabaseBackups = ({ user, noWrapper = false }) => {
  const modal = useModal();
  const userRole = getUserRole(user);
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;

  const [scope, setScope] = useState(isSuperAdmin ? 'central' : 'agency');
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupProgress, setBackupProgress] = useState('');
  const [backupHistory, setBackupHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restorePreview, setRestorePreview] = useState(null);

  useEffect(() => {
    if (isAdmin || isSuperAdmin) {
      fetchBackupHistory();
    }
  }, [isAdmin, isSuperAdmin]);

  useEffect(() => {
    if (backupHistory.length > 0) {
      const groups = groupBackupsByMonth(backupHistory);
      const init = {};
      Object.keys(groups).forEach((month, i) => {
        init[month] = i === 0;
      });
      setExpandedMonths(init);
    }
  }, [backupHistory]);

  const fetchBackupHistory = async () => {
    setHistoryLoading(true);
    try {
      const endpoint = isSuperAdmin ? '/api/super-admin/backups/list' : '/api/admin/backups/list';
      const response = await axios.get(endpoint, { withCredentials: true });
      setBackupHistory(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching backup history:', error);
      setBackupHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCreateBackup = async (uploadToDrive = false) => {
    const scopeLabel = scope === 'central' ? 'Central Database' : (user.agency_name || 'your agency');
    const actionLabel = uploadToDrive ? 'save to Velvet' : 'download';
    const confirmed = await modal.confirm(
      `Create a backup of ${scopeLabel} and ${actionLabel}? This may take several minutes depending on data size.`,
      'Create Backup'
    );

    if (!confirmed) return;

    setBackupLoading(true);
    setBackupProgress(uploadToDrive ? 'Creating backup and saving to Velvet...' : 'Creating backup...');

    try {
      if (uploadToDrive) {
        const endpoint = isSuperAdmin ? '/api/super-admin/backups/upload-to-drive' : '/api/admin/backups/upload-to-drive';
        const response = await axios.post(endpoint, { scope }, { withCredentials: true });
        setBackupProgress('');
        modal.alert(
          `Backup saved to Velvet successfully!\n\nFile: ${response.data.drive_file_name}`,
          'Backup Complete'
        );
        fetchBackupHistory();
      } else {
        const endpoint = isSuperAdmin ? '/api/super-admin/backups/create' : '/api/admin/backups/create';
        const response = await axios.post(endpoint, { scope }, {
          withCredentials: true,
          responseType: 'blob'
        });

        const contentDisposition = response.headers['content-disposition'];
        let filename = 'backup.zip';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch) filename = filenameMatch[1];
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        setBackupProgress('');
        modal.alert('Backup created and downloaded successfully!', 'Backup Complete');
        fetchBackupHistory();
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      setBackupProgress('');

      if (uploadToDrive) {
        modal.alert(error.response?.data?.error || 'Failed to save backup to Velvet', 'Backup Error');
      } else {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          try {
            const errorData = JSON.parse(text);
            modal.alert(errorData.error || 'Failed to create backup', 'Backup Error');
          } catch (e) {
            modal.alert('Failed to create backup', 'Backup Error');
          }
        } else {
          modal.alert(error.response?.data?.error || 'Failed to create backup', 'Backup Error');
        }
      }
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.velvet')) {
        modal.alert('Please select a valid Velvet backup file (.velvet)', 'Invalid File');
        return;
      }
      setRestoreFile(file);
      setRestorePreview(null);
    }
  };

  const handleTestRestore = async () => {
    if (!restoreFile) {
      modal.alert('Please select a backup file first', 'No File Selected');
      return;
    }

    setRestoreLoading(true);
    try {
      const formData = new FormData();
      formData.append('backup_zip', restoreFile);

      const endpoint = isSuperAdmin ? '/api/super-admin/backups/test-restore' : '/api/admin/backups/test-restore';
      const response = await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setRestorePreview(response.data);

      if (!response.data.valid) {
        modal.alert('This backup file appears to be invalid or corrupted', 'Invalid Backup');
      }
    } catch (error) {
      console.error('Error testing restore:', error);
      modal.alert(error.response?.data?.error || 'Failed to validate backup file', 'Validation Error');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile || !restorePreview || !restorePreview.valid) {
      modal.alert('Please test the backup file first', 'Validation Required');
      return;
    }

    const manifest = restorePreview.manifest;

    let confirmMessage = '';

    if (manifest.scope === 'central') {
      confirmMessage = `Are you sure you want to restore the CENTRAL DATABASE?\n\nBackup Date: ${new Date(manifest.created_at).toLocaleString()}\nCollections: ${Object.keys(manifest.collections || {}).length}\n\nWARNING: This will overwrite the central database affecting ALL agencies!`;
    } else {
      const targetAgencyName = manifest.agency_name;
      const backupAgencyId = manifest.agency_id;
      const userAgencyId = user.agency_id;

      confirmMessage = `RESTORE BACKUP\n\n`;
      confirmMessage += `Source: ${targetAgencyName}\n`;
      confirmMessage += `Target: ${targetAgencyName}\n\n`;
      confirmMessage += `Backup Date: ${new Date(manifest.created_at).toLocaleString()}\n`;
      confirmMessage += `Collections: ${Object.keys(manifest.collections || {}).length}\n`;
      confirmMessage += `Files: ${manifest.file_counts?.total || 0}\n\n`;

      if (!isSuperAdmin && backupAgencyId !== userAgencyId) {
        modal.alert(
          `This backup is from a different agency (${targetAgencyName}) and cannot be restored to your agency. Please select a backup file from your own agency.`,
          'Agency Mismatch'
        );
        return;
      }

      confirmMessage += `WARNING: This will completely overwrite all data for ${targetAgencyName}!`;
    }

    const confirmed = await modal.confirm(confirmMessage, 'Confirm Restore');
    if (!confirmed) return;

    setRestoreLoading(true);
    try {
      const formData = new FormData();
      formData.append('backup_zip', restoreFile);
      formData.append('confirmed', 'true');

      const endpoint = isSuperAdmin ? '/api/super-admin/backups/restore' : '/api/admin/backups/restore';
      await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      modal.alert('Backup restored successfully!', 'Restore Complete');
      setRestoreFile(null);
      setRestorePreview(null);
      fetchBackupHistory();
    } catch (error) {
      console.error('Error restoring backup:', error);
      modal.alert(error.response?.data?.error || 'Failed to restore backup', 'Restore Error');
    } finally {
      setRestoreLoading(false);
    }
  };

  const groupBackupsByMonth = (backups) => {
    const groups = {};
    backups.forEach(backup => {
      const date = new Date(backup.timestamp || backup.created_at);
      const key = date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = [];
      groups[key].push(backup);
    });
    return groups;
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({ ...prev, [month]: !prev[month] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatSize = (mb) => {
    if (!mb) return 'N/A';
    if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  if (!isAdmin && !isSuperAdmin) {
    return (
      <div className="database-backups-container">
        <div className="no-access">
          <Icon name="lock" size="3rem" />
          <h2>Access Denied</h2>
          <p>You need admin privileges to access database backups.</p>
        </div>
      </div>
    );
  }

  const groupedHistory = groupBackupsByMonth(backupHistory);

  const backupContent = (
    <div className="backup-two-column-layout">
      {/* Left Column */}
      <div className="backup-left-column">
        <div className="backup-action-section">
          <h2>Backup</h2>
          <p className="section-description">
            Choose what data should be included in this backup. You can create a complete backup
            of the central database or limit the backup to the current agency's sandbox environment.
            Select the option that best matches the data you want to preserve or transfer.
          </p>

          {isSuperAdmin && (
            <div className="scope-selector-row">
                <CustomSelect
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                >
                  <option value="central">Central Database</option>
                  <option value="agency">{user.agency_name || 'Current Agency'}</option>
                </CustomSelect>
            </div>
          )}

          <div className="backup-action-buttons">
            <button
              className="btn-outline"
              onClick={() => handleCreateBackup(false)}
              disabled={backupLoading}
            >
              Download Backup
            </button>
            <button
              className="btn-link-blue"
              onClick={() => handleCreateBackup(true)}
              disabled={backupLoading}
            >
              Save Backup to Velvet
            </button>
          </div>

          {backupProgress && (
            <div className="backup-progress">
              <div className="progress-spinner">
                <Icon name="spinner" size="1.25rem" />
              </div>
              <span>{backupProgress}</span>
            </div>
          )}
        </div>

        <div className="backup-section-divider" />

        <div className="recovery-section">
          <h2>Recovery</h2>
          <p className="section-description">
            Restore data from a Velvet backup file, or contact support for help with recovery.
          </p>

          {isSuperAdmin && (
            <div className="restore-controls">
              <div className="file-upload-group">
                <input
                  type="file"
                  accept=".velvet"
                  onChange={handleRestoreFile}
                  id="restore-file-input"
                />
                <label htmlFor="restore-file-input" className="btn-outline">
                  {restoreFile ? restoreFile.name : 'Choose Backup File'}
                </label>
                {restoreFile && (
                  <button
                    className="btn-link-blue"
                    onClick={handleTestRestore}
                    disabled={restoreLoading}
                  >
                    Test Backup File
                  </button>
                )}
              </div>

              {restorePreview && (
                <div className={`restore-preview ${restorePreview.valid ? 'valid' : 'invalid'}`}>
                  <div className="restore-preview-header">
                    <Icon name={restorePreview.valid ? 'check-circle' : 'alert-circle'} size="1.1rem" />
                    <span>{restorePreview.valid ? 'Valid Backup File' : 'Invalid Backup File'}</span>
                  </div>

                  {restorePreview.valid && restorePreview.manifest && (
                    <div className="preview-details">
                      <div className="detail-row">
                        <span>Agency</span>
                        <strong>{restorePreview.manifest.agency_name}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Backup Date</span>
                        <strong>{formatDate(restorePreview.manifest.created_at)}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Collections</span>
                        <strong>{Object.keys(restorePreview.manifest.collections || {}).length}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Total Size</span>
                        <strong>{formatSize(restorePreview.manifest.total_size_mb)}</strong>
                      </div>
                    </div>
                  )}

                  {!restorePreview.valid && restorePreview.errors && (
                    <div className="error-list">
                      {restorePreview.errors.map((error, idx) => (
                        <div key={idx} className="error-item">{error}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {restorePreview && restorePreview.valid && (
                <button
                  className="btn-danger-outline"
                  onClick={handleRestore}
                  disabled={restoreLoading}
                >
                  {restoreLoading ? 'Restoring...' : 'Restore Backup'}
                </button>
              )}
            </div>
          )}

          <a href="mailto:admin@myvelvet.agency" className="recovery-link">Recovery Help Center</a>
        </div>
      </div>

      {/* Right Column */}
      <div className="backup-right-column">
        <div className="backup-history-section">
          <h2>Backup History</h2>

          {historyLoading ? (
            <div className="loading-state">Loading backup history...</div>
          ) : backupHistory.length === 0 ? (
            <div className="empty-state">
              <Icon name="database" size="3rem" />
              <p>No backups created yet</p>
            </div>
          ) : (
            <div className="backup-table">
              <table>
                <thead>
                  <tr>
                    <th>DATE</th>
                    <th>CREATED BY</th>
                    <th>SCOPE</th>
                    <th>SIZE</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedHistory).map(([month, backups]) => (
                    <React.Fragment key={month}>
                      <tr className="month-group-row" onClick={() => toggleMonth(month)}>
                        <td colSpan="5">
                          <div className="month-group-content">
                            <span className="month-group-label">{month}</span>
                            <Icon
                              name="chevron-down"
                              size="0.85rem"
                              className={`month-chevron ${expandedMonths[month] ? 'expanded' : ''}`}
                            />
                          </div>
                        </td>
                      </tr>
                      {expandedMonths[month] && backups.map((backup) => {
                        const scopeDisplay = backup.details?.scope === 'central' ? 'Central' : 'Sandbox';
                        const isSaved = backup.details?.uploaded_to_company_drive;
                        return (
                          <tr key={backup._id} className="backup-data-row">
                            <td>{formatDate(backup.timestamp || backup.created_at)}</td>
                            <td>{backup.user_email || 'N/A'}</td>
                            <td>{scopeDisplay}</td>
                            <td>{formatSize(backup.details?.size_mb)}</td>
                            <td>
                              <span className={`status-badge ${isSaved ? 'status-saved' : 'status-local'}`}>
                                {isSaved ? 'SAVED TO VELVET' : 'LOCAL DOWNLOAD'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (noWrapper) return backupContent;
  return <div className="database-backups-container">{backupContent}</div>;
};

export default DatabaseBackups;
